import { useMemo } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { MapPin, Scale } from "lucide-react";
import { api } from "@/lib/api";
import { useSeo } from "@/lib/seo";
import StoriesRail from "@/components/lux/StoriesRail";
import ResultsGrid from "@/components/lux/ResultsGrid";
import Filters, { filtersToQuery, readFilters } from "@/components/lux/Filters";
import SafetyCallout from "@/components/lux/SafetyCallout";
import NotFoundPage from "./NotFoundPage";

export default function CityPage({ mode = "city" }) {
  const { city } = useParams();
  const [params, setParams] = useSearchParams();
  const filters = useMemo(() => readFilters(params), [params]);
  const qs = params.toString();
  const { data, isLoading, isFetching, error } = useQuery({
    queryKey: ["city", city, qs],
    queryFn: async () => (await api.get(`/public/cities/${city}${qs ? `?${qs}` : ""}`)).data,
    placeholderData: keepPreviousData,
    retry: (count, err) => err?.response?.status !== 404 && count < 2,
  });
  const isBrowse = mode === "browse";
  const seo = data?.seo;
  const name = data?.city?.name || "";
  useSeo(seo ? (isBrowse
    ? { title: `Browse all verified advertisers in ${name} | LUX`, description: seo.description, canonical: seo.canonical.replace(new RegExp(`/${city}$`), `/${city}/browse`), robots: "noindex,follow" }
    : seo) : { title: "LUX" });
  const apply = (f) => setParams(filtersToQuery(f), { replace: true });

  if (error?.response?.status === 404) return <NotFoundPage />;

  return (
    <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
      <div className="pb-6 pt-10 sm:pt-14">
        <nav className="text-xs text-lux-muted" aria-label="Breadcrumb">
          <Link to="/" className="hover:text-lux-silverBright">Home</Link> <span className="mx-1.5">/</span>
          {isBrowse ? <><Link to={`/${city}`} className="hover:text-lux-silverBright">{name}</Link> <span className="mx-1.5">/</span> <span className="text-lux-text">Browse</span></> : <span className="text-lux-text">{name}</span>}
        </nav>
        <p className="lux-label mt-6">England · {data?.results ? `${data.results.length} advertisers` : "Loading"}</p>
        <h1 className="mt-3 font-serif text-5xl leading-none sm:text-6xl" data-testid="city-title">{isBrowse ? `Browse ${name}` : name || " "}</h1>
        {!isBrowse && data?.city?.tagline && <p className="mt-3 text-lg text-lux-silver">{data.city.tagline}</p>}
        {!isBrowse && data?.city?.intro && <p className="mt-6 max-w-3xl text-[15px] leading-relaxed text-lux-text/80" data-testid="city-intro">{data.city.intro}</p>}
        {data?.other_cities?.length > 0 && (
          <p className="mt-5 flex flex-wrap items-center gap-2 text-xs text-lux-muted" data-testid="city-switcher">
            Also on LUX:
            {data.other_cities.map((c) => <Link key={c.slug} to={`/${c.slug}${isBrowse ? "/browse" : ""}`} className="lux-chip py-1 hover:border-lux-silver/60" data-testid={`city-switch-${c.slug}`}>{c.name}</Link>)}
          </p>
        )}
      </div>

      {!isBrowse && data?.stories?.length > 0 && <StoriesRail groups={data.stories} title={`Today's stories in ${name}`} />}

      {!isBrowse && (
        <div className="lux-scroll-x -mx-4 mt-6 flex gap-2 overflow-x-auto px-4 pb-1" aria-label="Areas">
          {(data?.areas || []).map((a) => (
            <Link key={a.slug} to={`/${city}/${a.slug}`} className="lux-chip shrink-0 py-2 hover:border-lux-silver/60" data-testid={`city-area-chip-${a.slug}`}>
              <MapPin className="h-3 w-3 text-lux-silver" /> {a.name} <span className="text-lux-muted">{a.count}</span>
            </Link>
          ))}
        </div>
      )}

      <div className="mt-8">
        <Filters value={filters} onApply={apply} areas={(data?.areas || []).map((a) => ({ slug: a.slug, name: a.name }))} showArea total={data?.results?.length} />
      </div>
      <p className="mt-3 flex items-center gap-1.5 text-xs text-lux-muted" data-testid="city-ranking-note"><Scale className="h-3.5 w-3.5 text-lux-silver" aria-hidden="true" /> Ordered by availability today, then verification freshness. No paid placement.</p>
      <div className={`mt-6 transition-opacity ${isFetching && !isLoading ? "opacity-60" : "opacity-100"}`}>
        <ResultsGrid cards={data?.results} loading={isLoading} onClear={() => apply({})} />
      </div>
      <div className="mt-20"><SafetyCallout /></div>
    </div>
  );
}
